import {
  useEffect,
  useRef,
  useState,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * Renders ```mermaid fenced code blocks as diagrams inside the chat + artifact
 * markdown surfaces. Mermaid is a large dependency, so it is dynamically imported
 * (code-split) and initialized once, the first time a diagram actually appears.
 */

const MERMAID_LANG_CLASS = 'language-mermaid';

/** True when a react-markdown `code` element is a ```mermaid block. */
export function isMermaidCode(className?: string): boolean {
  return typeof className === 'string' && className.split(/\s+/).includes(MERMAID_LANG_CLASS);
}

/**
 * react-markdown wraps fenced code in `<pre><code>`. When the inner block is a
 * mermaid diagram we render block-level SVG, so the `<pre>` wrapper — which forces
 * a monospace scroll box — must step aside and render its child directly.
 *
 * We scan for the first element child rather than assuming `children[0]`: depending
 * on the remark/rehype pipeline the `<pre>` can receive a leading whitespace text
 * node before the `<code>` element.
 */
export function preChildIsMermaid(children: ReactNode): boolean {
  const nodes = Array.isArray(children) ? children : [children];
  const element = nodes.find((node): node is ReactElement<{ className?: string }> =>
    isValidElement<{ className?: string }>(node)
  );
  return element ? isMermaidCode(element.props.className) : false;
}

let mermaidLoader: Promise<typeof import('mermaid').default> | null = null;

function loadMermaid(): Promise<typeof import('mermaid').default> {
  if (mermaidLoader === null) {
    mermaidLoader = import('mermaid')
      .then(mod => {
        mod.default.initialize({
          startOnLoad: false,
          theme: 'dark',
          // 'strict' runs mermaid's rendered SVG through DOMPurify (bundled inside
          // mermaid — see its render path) and disables inline scripts/HTML labels.
          // Diagrams can originate from workflow artifacts and assistant messages,
          // so the SVG handed to dangerouslySetInnerHTML below is sanitized at source.
          securityLevel: 'strict',
          fontFamily: 'inherit',
        });
        return mod.default;
      })
      .catch((err: unknown) => {
        // Never cache a rejected import/init: reset so the next diagram mount can
        // retry, rather than every future diagram inheriting one poisoned promise.
        mermaidLoader = null;
        throw err;
      });
  }
  return mermaidLoader;
}

// Monotonic id source. mermaid.render(id, …) injects a temporary DOM node keyed by
// id; a fresh id per render avoids collisions when a diagram re-renders rapidly
// (e.g. streaming chat content) while a prior render is still in flight.
let renderSeq = 0;

interface MermaidProps {
  chart: string;
}

export function Mermaid({ chart }: MermaidProps): React.ReactElement {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bindFunctionsRef = useRef<((element: Element) => void) | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setError(null);
    bindFunctionsRef.current = undefined;
    const renderId = `mermaid-${(renderSeq += 1)}`;

    void (async (): Promise<void> => {
      try {
        const mermaid = await loadMermaid();
        const { svg: rendered, bindFunctions } = await mermaid.render(renderId, chart);
        if (!cancelled) {
          bindFunctionsRef.current = bindFunctions;
          setSvg(rendered);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to render diagram');
        }
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [chart]);

  // Mermaid attaches interaction handlers (clickable nodes, tooltips) via the
  // bindFunctions callback, which must run against the SVG after it's in the DOM.
  useEffect(() => {
    if (svg !== null && bindFunctionsRef.current && containerRef.current) {
      bindFunctionsRef.current(containerRef.current);
    }
  }, [svg]);

  if (error !== null) {
    return (
      <div className="my-3 rounded-lg border border-error/40 bg-error/5 p-3">
        <p className="mb-2 text-sm text-error">Diagram failed to render: {error}</p>
        <pre className="overflow-x-auto font-mono text-xs text-text-secondary">{chart}</pre>
      </div>
    );
  }

  if (svg === null) {
    return (
      <div className="my-3 rounded-lg border border-border bg-surface p-4 text-sm text-text-secondary">
        Rendering diagram…
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="mermaid-diagram my-3 flex justify-center overflow-x-auto rounded-lg border border-border bg-surface p-4"
      // Mermaid output is sanitized (securityLevel: 'strict'); rendering its SVG requires innerHTML.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
